import {
    GroupQueueEntry,
    useQueueTimeContext,
} from './useQueueTimeContext'
import {
    getQueueIdsForTicketing,
    getTicketDurationCommentRequests,
} from './ticketDurationData'
import {
    useAppDispatch,
    useAppSelectorWithShallowEquality,
} from '../index'
import {NinchatProperties} from 'api/types'
import React from 'react'
import {getAudienceRangeComments} from 'api/action/getAudienceRangeComments'
import {getQueueActivity} from 'api/action/getQueueActivity'

type TicketDurationCommentRequest = ReturnType<typeof getTicketDurationCommentRequests>[number]

const getCommentRequestKey = ({
    audience_first,
    audience_last,
    queue_id,
}: TicketDurationCommentRequest): string => `${queue_id}:${audience_first}:${audience_last}`

const getTicketActivityKey = ({
    groupQueues,
    queue,
    queueIds,
    queueId,
}: {
    groupQueues: GroupQueueEntry[]
    queue: NinchatProperties.NinchatUserQueue | undefined
    queueIds: NinchatProperties.QueueID[]
    queueId: NinchatProperties.QueueID
}): string => {
    if (!queue) {
        return ''
    }

    if (!queue.queue_attrs.is_group) {
        return queueIds.length > 0
            ? `${queueId}:${queue.queue_attrs.unassigned_registered ?? 0}`
            : ''
    }

    const queueIdSet = new Set(queueIds)

    return groupQueues
        .filter((groupQueue) => queueIdSet.has(groupQueue.id))
        .map((groupQueue) => `${groupQueue.id}:${groupQueue.queueProps.queue_attrs.unassigned_registered ?? 0}`)
        .sort()
        .join('|')
}

export const useEnsureTicketDurationData = (
    queue_id: NinchatProperties.QueueID,
    enabled: boolean,
): void => {
    const dispatch = useAppDispatch()
    const requestedCommentKeysRef = React.useRef<Set<string>>(new Set())
    const previousTicketActivitySignatureRef = React.useRef<string | undefined>(undefined)
    const {
        groupQueues,
        parentGroupQueue,
        queue,
    } = useQueueTimeContext(queue_id)
    const queueActivities = useAppSelectorWithShallowEquality((state) => state.ninchat._user_queues_activity)
    const audienceComments = useAppSelectorWithShallowEquality((state) => state.ninchat._user_audience_comments)
    const audienceTranscripts = useAppSelectorWithShallowEquality((state) => ({
        ...state.ninchat._audience_transcripts,
        ...state.ninchat._user_audience_transcripts,
    }))

    const queueIds = React.useMemo(() => (enabled
        ? getQueueIdsForTicketing({
            groupQueues,
            parentGroupQueue,
            queue,
            queueId: queue_id,
        })
        : []), [enabled, groupQueues, parentGroupQueue, queue, queue_id])

    const missingActivityQueueIds = React.useMemo(() => queueIds.filter((activityQueueId) => queueActivities[activityQueueId] === undefined), [queueActivities, queueIds])
    const loadedActivityQueueIds = React.useMemo(() => queueIds.filter((activityQueueId) => queueActivities[activityQueueId] !== undefined), [queueActivities, queueIds])
    const ticketActivitySignature = React.useMemo(() => getTicketActivityKey({
        groupQueues,
        queue,
        queueId: queue_id,
        queueIds,
    }), [
        groupQueues,
        queue,
        queueIds,
        queue_id,
    ])

    React.useEffect(() => {
        if (!enabled) {
            previousTicketActivitySignatureRef.current = undefined
            return
        }

        const previousTicketActivitySignature = previousTicketActivitySignatureRef.current
        const hasPreviousTicketActivitySignature = previousTicketActivitySignature !== undefined
        const signatureChanged = hasPreviousTicketActivitySignature && previousTicketActivitySignature !== ticketActivitySignature
        // If ticket counts changed, refresh already-loaded activity too;
        // otherwise only fetch activity that has not been loaded yet.
        const activityQueueIds = signatureChanged
            ? [
                ...missingActivityQueueIds,
                ...loadedActivityQueueIds,
            ]
            : missingActivityQueueIds

        previousTicketActivitySignatureRef.current = ticketActivitySignature

        if (activityQueueIds.length === 0) {
            return
        }

        activityQueueIds.forEach((activityQueueId) => {
            dispatch(getQueueActivity({queue_id: activityQueueId}))
        })
    }, [
        dispatch,
        enabled,
        missingActivityQueueIds,
        loadedActivityQueueIds,
        ticketActivitySignature,
    ])

    const commentRequests = React.useMemo(() => (enabled
        ? getTicketDurationCommentRequests({
            audienceComments,
            audienceTranscripts,
            queueActivities,
            queueIds,
        })
        : []), [
        audienceComments,
        audienceTranscripts,
        enabled,
        queueActivities,
        queueIds.length,
        queueIds,
    ])

    React.useEffect(() => {
        if (!enabled || commentRequests.length === 0) {
            return
        }

        commentRequests.forEach((request) => {
            const requestKey = getCommentRequestKey(request)

            if (requestedCommentKeysRef.current.has(requestKey)) {
                return
            }

            requestedCommentKeysRef.current.add(requestKey)
            dispatch(getAudienceRangeComments({
                ...request,
                history_length: 50,
            })).unwrap()
                .catch(() => {
                    // Allow failed comment requests to be retried on a later render.
                    requestedCommentKeysRef.current.delete(requestKey)
                })
        })
    }, [
        commentRequests,
        dispatch,
        enabled,
    ])
}
